import React, { useEffect, useRef, useState } from "react";
import { useThemeProvider } from "../../../../utils/ThemeContext";

import {
  Chart,
  Filler,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  TimeScale,
  Tooltip,
} from "chart.js";
import "chartjs-adapter-moment";
import { chartColors } from "./ChartjsConfig";

// Import utilities
import { hexToRGB, tailwindConfig } from "../../../../utils/Utils";
import theme from "@shared/utils/themeColors";

Chart.register(
  LineController,
  LineElement,
  Filler,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip
);

function RealtimeChart({ data, width, height, processData }) {
  const [chart, setChart] = useState(null);
  const canvas = useRef(null);
  const chartValue = useRef(null);
  const chartDeviation = useRef(null);
  const { currentTheme } = useThemeProvider();
  const darkMode = currentTheme === "dark";
  const {
    textColor,
    gridColor,
    tooltipTitleColor,
    tooltipBodyColor,
    tooltipBgColor,
    tooltipBorderColor,
  } = chartColors;

  useEffect(() => {
    const ctx = canvas.current;
    // eslint-disable-next-line no-unused-vars
    const newChart = new Chart(ctx, {
      type: "line",
      data: data,
      options: {
        layout: {
          padding: 20,
        },
        scales: {
          y: {
            border: {
              display: false,
            },
            suggestedMin: 0,
            suggestedMax: 100,
            ticks: {
              maxTicksLimit: 5,
              callback: (value) => `${value}%`,
              color: darkMode ? textColor.dark : textColor.light,
            },
            grid: {
              color: darkMode ? gridColor.dark : gridColor.light,
            },
          },
          x: {
            type: "time",
            time: {
              parser: "yyyy-MM-dd",
              unit: "day",
              tooltipFormat: "MMM DD, YYYY",
              displayFormats: {
                day: "MMM DD",
              },
            },
            border: {
              display: false,
            },
            grid: {
              display: false,
            },
            ticks: {
              autoSkipPadding: 48,
              maxRotation: 0,
              color: darkMode ? textColor.dark : textColor.light,
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            titleFont: {
              weight: 600,
            },
            callbacks: {
              title: (context) => {
                const testName = processData[context[0].dataIndex].testName;
                return (
                  context[0].label +
                  ": " +
                  testName.substring(0, 25) +
                  (testName.length > 25 ? "..." : "")
                );
              },
              label: (context) =>
                `${context.parsed.y.toFixed(1)}% correct answers`,
            },
            titleColor: darkMode
              ? tooltipTitleColor.dark
              : tooltipTitleColor.light,
            bodyColor: darkMode
              ? tooltipBodyColor.dark
              : tooltipBodyColor.light,
            backgroundColor: darkMode
              ? tooltipBgColor.dark
              : tooltipBgColor.light,
            borderColor: darkMode
              ? tooltipBorderColor.dark
              : tooltipBorderColor.light,
          },
        },
        interaction: {
          intersect: false,
          mode: "nearest",
        },
        maintainAspectRatio: false,
      },
    });
    setChart(newChart);
    return () => newChart.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Update header values
  useEffect(() => {
    // Calculate current average (last 3 values or all if less than 3)
    const values = data.datasets[0].data;

    // Calculate total average of all values
    const totalAverage =
      values.length > 0
        ? values.reduce((sum, val) => sum + (val || 0), 0) / values.length
        : 0;

    // Ensure we don't divide by zero
    const safeAvg = totalAverage || 1;

    if (chartValue.current) {
      chartValue.current.innerHTML = `${safeAvg.toFixed(1)}%`;
    }

    // Since we're showing the total average, there's no previous value to compare
    const diff = 0;

    if (chartDeviation.current) {
      if (diff < 0) {
        chartDeviation.current.style.backgroundColor = `rgba(${hexToRGB(
          tailwindConfig().theme.colors.red[500]
        )}, 0.2)`;
        chartDeviation.current.style.color =
          tailwindConfig().theme.colors.red[700];
      } else {
        chartDeviation.current.style.backgroundColor = `rgba(${hexToRGB(
          tailwindConfig().theme.colors.green[500]
        )}, 0.2)`;
        chartDeviation.current.style.color =
          tailwindConfig().theme.colors.green[700];
      }
      chartDeviation.current.innerHTML = `${diff > 0 ? "+" : ""}${diff.toFixed(
        1
      )}%`;
    }
  }, [data]);

  useEffect(() => {
    if (!chart) return;

    if (darkMode) {
      chart.options.scales.x.ticks.color = textColor.dark;
      chart.options.scales.y.ticks.color = textColor.dark;
      chart.options.scales.y.grid.color = gridColor.dark;
      chart.options.plugins.tooltip.titleColor = tooltipTitleColor.dark;
      chart.options.plugins.tooltip.bodyColor = tooltipBodyColor.dark;
      chart.options.plugins.tooltip.backgroundColor = tooltipBgColor.dark;
      chart.options.plugins.tooltip.borderColor = tooltipBorderColor.dark;
    } else {
      chart.options.scales.x.ticks.color = textColor.light;
      chart.options.scales.y.ticks.color = textColor.light;
      chart.options.scales.y.grid.color = gridColor.light;
      chart.options.plugins.tooltip.titleColor = tooltipTitleColor.light;
      chart.options.plugins.tooltip.bodyColor = tooltipBodyColor.light;
      chart.options.plugins.tooltip.backgroundColor = tooltipBgColor.light;
      chart.options.plugins.tooltip.borderColor = tooltipBorderColor.light;
    }
    chart.update("none");
  }, [currentTheme]);

  return (
    <React.Fragment>
      <div className="px-5 py-3">
        <div className="flex items-start">
          <div className={`text-3xl font-bold ${theme.text.primary} mr-2 tabular-nums`}>
            <span ref={chartValue}>0%</span>
          </div>
          <div className={theme.text.muted}>
            Correct answers
          </div>
        </div>
      </div>
      <div className="grow">
        <canvas ref={canvas} width={width} height={height}></canvas>
      </div>
    </React.Fragment>
  );
}

export default RealtimeChart;
